import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Heart, Plane, Clock, PartyPopper } from 'lucide-react';
import FleetImage from '@/components/FleetImage';
import servicesService from '../lib/servicesService';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const OurServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchServices = async () => {
    try {
      const data = await servicesService.getServices();
      if (data && data.length > 0) {
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get image for a specific service
  const getServiceImage = (serviceName) => {
    // Don't try to find services if they haven't been loaded yet
    if (loading || services.length === 0) {
      return null;
    }
    
    const service = services.find(s => s.name === serviceName);
    return service?.image_url || null;
  };

  useEffect(() => {
    fetchServices();
    
    // Listen for services data updates from admin
    const handleServicesUpdate = () => {
      fetchServices();
    };
    
    window.addEventListener('servicesDataUpdated', handleServicesUpdate);
    
    return () => {
      window.removeEventListener('servicesDataUpdated', handleServicesUpdate);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Services - Akiblackcar</title>
        <meta name="description" content="Akiblackcar offers a variety of luxury transportation services including wedding limos, corporate travel, airport transfers, hourly charters, and transportation for special events." />
      </Helmet>
      <motion.div initial="hidden" animate="visible" variants={fadeIn} className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold font-serif text-brand-gold">Our Services</h1>
          <p className="mt-4 text-lg text-gray-300 max-w-3xl mx-auto">
            Tailored transportation solutions designed for every occasion. We provide unparalleled service to ensure your journey is perfect.
          </p>
        </div>

        <div className="space-y-16">
          <motion.div
            key="Wedding Services"
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            transition={{ delay: 0 }}
          >
            <Card className="bg-brand-charcoal border-brand-gold/20 overflow-hidden w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                <div className="order-1 md:order-1">
                  <FleetImage
                    key={`wedding-${services.length}`}
                    src={getServiceImage("Wedding Service") || "https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                    alt="Wedding limousine with bride and groom"
                    vehicleType="Limousine"
                    vehicleName="Stretch Limousine"
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 order-2 md:order-2">
                  <div className="flex items-center mb-4">
                    <Heart className="w-10 h-10 mr-4 text-brand-gold" />
                    <CardTitle className="text-3xl font-serif text-white">Wedding Services</CardTitle>
                  </div>
                  <CardContent className="p-0">
                    <p className="text-gray-300 text-lg mb-6">Arrive in elegance and style on your special day. Our wedding packages provide luxurious, reliable transportation for the bride, groom, and guests, ensuring a flawless and memorable experience.</p>
                    <Link to="/booking">
                      <Button size="lg" className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold">
                        Book This Service <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            key="Corporate Travel"
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-brand-charcoal border-brand-gold/20 overflow-hidden w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                <div className="order-1 md:order-2">
                  <FleetImage
                    key={`corporate-${services.length}`}
                    src={getServiceImage("Corporate Events") || "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                    alt="Executive sedan for corporate travel"
                    vehicleType="Sedan"
                    vehicleName="Executive Sedan"
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 order-2 md:order-1">
                  <div className="flex items-center mb-4">
                    <Briefcase className="w-10 h-10 mr-4 text-brand-gold" />
                    <CardTitle className="text-3xl font-serif text-white">Corporate Travel</CardTitle>
                  </div>
                  <CardContent className="p-0">
                    <p className="text-gray-300 text-lg mb-6">Impress clients and colleagues with our professional corporate travel services. We offer punctual, discreet, and comfortable transportation for business meetings, conferences, and executive travel.</p>
                    <Link to="/booking">
                      <Button size="lg" className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold">
                        Book This Service <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            key="Airport Transfers"
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-brand-charcoal border-brand-gold/20 overflow-hidden w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                <div className="order-1 md:order-1">
                  <FleetImage
                    key={`airport-${services.length}`}
                    src={getServiceImage("Airport Transfer") || "https://images.unsplash.com/photo-1555215695-3004980ad54e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                    alt="Luxury SUV for airport transfers"
                    vehicleType="SUV"
                    vehicleName="Luxury SUV"
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 order-2 md:order-2">
                  <div className="flex items-center mb-4">
                    <Plane className="w-10 h-10 mr-4 text-brand-gold" />
                    <CardTitle className="text-3xl font-serif text-white">Airport Transfers</CardTitle>
                  </div>
                  <CardContent className="p-0">
                    <p className="text-gray-300 text-lg mb-6">Start or end your journey with ease. Our airport transfer service guarantees timely pickups and drop-offs to all major airports in the DMV area, with flight tracking to accommodate any delays.</p>
                    <Link to="/booking">
                      <Button size="lg" className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold">
                        Book This Service <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            key="Hourly Service"
            custom={3}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-brand-charcoal border-brand-gold/20 overflow-hidden w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                <div className="order-1 md:order-2">
                  <FleetImage
                    key={`hourly-${services.length}`}
                    src={getServiceImage("Hourly Service") || "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"}
                    alt="Luxury coach bus for hourly service"
                    vehicleType="Bus"
                    vehicleName="Coach Bus"
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 order-2 md:order-1">
                  <div className="flex items-center mb-4">
                    <Clock className="w-10 h-10 mr-4 text-brand-gold" />
                    <CardTitle className="text-3xl font-serif text-white">Hourly Service</CardTitle>
                  </div>
                  <CardContent className="p-0">
                    <p className="text-gray-300 text-lg mb-6">Enjoy the flexibility of having a private chauffeur at your disposal. Our hourly "as-directed" service is perfect for a day of shopping, multiple meetings, or a personalized city tour.</p>
                    <Link to="/booking">
                      <Button size="lg" className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold">
                        Book This Service <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            key="Special Events"
            custom={4}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-brand-charcoal border-brand-gold/20 overflow-hidden w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                <div className="order-1 md:order-1">
                  <FleetImage
                    key={`special-${services.length}`}
                    src={getServiceImage("Special Events") || "/images/fleet/limousine/optimized/stretch-limousine.webp"}
                    alt="Limousine at night in Washington D.C. for a special event"
                    vehicleType="Limousine"
                    vehicleName="Stretch Limousine"
                    className="w-full h-80 object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 order-2 md:order-2">
                  <div className="flex items-center mb-4">
                    <PartyPopper className="w-10 h-10 mr-4 text-brand-gold" />
                    <CardTitle className="text-3xl font-serif text-white">Special Events</CardTitle>
                  </div>
                  <CardContent className="p-0">
                    <p className="text-gray-300 text-lg mb-6">From concerts and sporting events to proms and anniversaries, make any occasion extraordinary. Travel in a group or enjoy an intimate ride, we handle the driving so you can enjoy the event.</p>
                    <Link to="/booking">
                      <Button size="lg" className="bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold">
                        Book This Service <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

      </motion.div>
    </>
  );
};

export default OurServices;